import { DateTimeEditor, DropdownEditor, TextEditor } from '@approvalmax/ui';
import { constants, selectors } from 'modules/common';
import { domain } from 'modules/data';
import { RequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { FC, memo } from 'react';
import bemFactory from 'react-bem-factory';

import BankAccountDataProvider from '../../../dataProviders/BankAccountDataProvider';
import { useMainInformationData } from './MainInformation.hooks';
import { messages } from './MainInformation.messages';
import { FieldOrdered, Row } from './MainInformation.styles';
import { MainInformationProps } from './MainInformation.types';

const { xeroConstants } = constants;

const qa = bemFactory.qa('xero-bill-batch-payment-creation-request-main-information');

const MainInformation: FC<MainInformationProps> = memo((props) => {
    const { countryCode } = props;

    const {
        billBatchPayment,
        showErrors,
        onPaymentDateChange,
        onBankAccountChange,
        onNarrativeChange,
        onDetailsChange,
        onParticularsChange,
        onCodeChange,
        onReferenceChange,
        referenceFieldMaxLength,
        isInvalidNarrative,
        isInvalidReference,
        isInvalidDetails,
        isInvalidParticulars,
        isInvalidCode,
        isUkCompany,
        editMode,
    } = useMainInformationData(countryCode);

    return (
        <Row>
            <FieldOrdered required title={messages.paymentDate} order={1}>
                <DateTimeEditor
                    disabled={editMode === RequestEditMode.Approver}
                    invalid={showErrors && !billBatchPayment.paymentDate}
                    qa={qa('payment-date-editor')}
                    value={billBatchPayment.paymentDate || ''}
                    onChange={onPaymentDateChange}
                    placeholder={messages.paymentDatePlaceholder}
                />
            </FieldOrdered>

            <FieldOrdered required title={messages.bankAccountLabel} order={2}>
                <BankAccountDataProvider>
                    <DropdownEditor
                        disabled={editMode === RequestEditMode.Approver}
                        invalid={showErrors && !billBatchPayment.bankAccount}
                        qa={qa('bank-account-editor')}
                        value={billBatchPayment.bankAccount}
                        onChange={onBankAccountChange}
                        placeholder={messages.bankAccountPlaceholder}
                    />
                </BankAccountDataProvider>
            </FieldOrdered>

            {selectors.field.getSupportRegionalFieldByCountryCode(countryCode, domain.RegionalField.Narrative) && (
                <FieldOrdered title={messages.narrativeLabel} order={isUkCompany ? 4 : 3}>
                    <TextEditor
                        qa={qa('narration-editor')}
                        maxLength={xeroConstants.BATCH_PAYMENT_NARRATIVE_MAX_LENGTH}
                        value={billBatchPayment.narrative || ''}
                        onChange={onNarrativeChange}
                        placeholder={messages.narrativePlaceholder}
                        disabled={editMode === RequestEditMode.Approver}
                        invalid={showErrors && isInvalidNarrative}
                    />
                </FieldOrdered>
            )}

            {selectors.field.getSupportRegionalFieldByCountryCode(countryCode, domain.RegionalField.Details) && (
                <FieldOrdered title={messages.detailsLabel} order={isUkCompany ? 3 : 4}>
                    <TextEditor
                        qa={qa('details-editor')}
                        maxLength={xeroConstants.BATCH_PAYMENT_DETAILS_MAX_LENGTH}
                        value={billBatchPayment.details || ''}
                        onChange={onDetailsChange}
                        placeholder={messages.detailsPlaceholder}
                        disabled={editMode === RequestEditMode.Approver}
                        invalid={showErrors && isInvalidDetails}
                    />
                </FieldOrdered>
            )}

            {selectors.field.getSupportRegionalFieldByCountryCode(countryCode, domain.RegionalField.Particulars) && (
                <FieldOrdered title={messages.particularsLabel} order={5}>
                    <TextEditor
                        qa={qa('particulars-editor')}
                        maxLength={xeroConstants.BATCH_PAYMENT_PARTICULARS_MAX_LENGTH}
                        value={billBatchPayment.particulars || ''}
                        onChange={onParticularsChange}
                        placeholder={messages.particularsPlaceholder}
                        disabled={editMode === RequestEditMode.Approver}
                        invalid={showErrors && isInvalidParticulars}
                    />
                </FieldOrdered>
            )}

            {selectors.field.getSupportRegionalFieldByCountryCode(countryCode, domain.RegionalField.Code) && (
                <FieldOrdered title={messages.codeLabel} order={6}>
                    <TextEditor
                        qa={qa('code-editor')}
                        maxLength={xeroConstants.BATCH_PAYMENT_CODE_MAX_LENGTH}
                        value={billBatchPayment.code || ''}
                        onChange={onCodeChange}
                        placeholder={messages.codePlaceholder}
                        disabled={editMode === RequestEditMode.Approver}
                        invalid={showErrors && isInvalidCode}
                    />
                </FieldOrdered>
            )}

            {selectors.field.getSupportRegionalFieldByCountryCode(countryCode, domain.RegionalField.Reference) && (
                <FieldOrdered title={messages.referenceLabel} order={7}>
                    <TextEditor
                        qa={qa('reference-editor')}
                        maxLength={referenceFieldMaxLength}
                        value={billBatchPayment.reference || ''}
                        onChange={onReferenceChange}
                        placeholder={messages.referencePlaceholder}
                        disabled={editMode === RequestEditMode.Approver}
                        invalid={showErrors && isInvalidReference}
                    />
                </FieldOrdered>
            )}
        </Row>
    );
});

export default MainInformation;
